import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  PaginationState,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
  Table,
  TableController,
} from '@tanstack/lit-table'
// Extended column definition with textAlign property
export type ExtendedColumnDef<T> = ColumnDef<T, unknown> & {
  textAlign?: string
  headerTextAlign?: string
  disableDefaultSorting?: boolean
  /**
   * Pins the column to the left or right edge of the horizontally scrolling
   * viewport, so it stays visible while the other columns scroll under it.
   * Give a pinned column an explicit `size` — its width is what positions the
   * next column pinned to the same side. Columns pinned to one side stack in
   * DOM order out from that edge.
   */
  sticky?: 'left' | 'right'
}

import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { t } from '@/localization/localization'

import DapDSCheckbox from '../checkbox/checkbox.component'
import { IconSize } from '../icon/icon.component'
import ArrowsArrowDownSLine from '../icons/arrows/arrows-arrow-down-s-line/arrows-arrow-down-s-line.component'
import ArrowsArrowUpSLine from '../icons/arrows/arrows-arrow-up-s-line/arrows-arrow-up-s-line.component'
import DapDSPager from '../pager/pager.component'
import DapDSSkeleton from '../skeleton/skeleton.component'
import { SpinnerVariant } from '../spinner/spinner.component'
import DapDSSpinner from '../spinner/spinner.component'
import styles from './datatable.scss?inline'
import { renderCellContent } from './utils/cell-renderer'

const MULTISELECT_COLUMN_ID = '__multiSelect__'

// Type alias for sort state
type SortState = false | 'asc' | 'desc'
type LoadingType = 'spinner' | 'skeleton'

/**
 * `dap-ds-datatable`
 * @summary A data table is a component that displays data in a tabular format.
 * @element dap-ds-datatable
 * @title - Data table
 * @generic T
 *
 * @event {{ sorting: SortingState }} dds-sorting-change - Fired when the sorting of the table changes.
 * @event {{ selection: RowSelectionState }} dds-selection-change - Fired when the selection of the table changes.
 * @event {{ pagination: PaginationState }} dds-pagination-change - Fired when the pagination of the table changes.
 * @event {{ row: Row<T> }} dds-row-click - Fired when a row is clicked.
 *
 * @slot loading - The loading content of the table.
 * @slot empty - The empty content of the table.
 *
 * @csspart base - The main table container.
 * @csspart header - The header of the table.
 * @csspart header-row - The header row of the table.
 * @csspart header-cell - All cells of the header.
 * @csspart body - The body of the table.
 * @csspart empty - The empty content of the table.
 * @csspart loading - The loading content of the table.
 * @csspart row - All rows of the table.
 * @csspart cell - All cells of the table.
 * @csspart pager - The pager of the table.
 * @csspart pager-base - The base of the pager.
 * @csspart pager-first - The first button of the pager.
 * @csspart pager-previous - The previous button of the pager.
 * @csspart pager-next - The next button of the pager.
 * @csspart pager-last - The last button of the pager.
 * @csspart pager-page-size-select - The page size select of the pager.
 *
 * @cssproperty --dds-datatable-bg-color - Background color of the table. (default: var(--dds-background-neutral-subtle)).
 * @cssproperty --dds-datatable-border-color - Border color of the table cells. (default: var(--dds-border-neutral-divider))
 * @cssproperty --dds-datatable-header-bg-color - Background color of the table header. (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-datatable-header-text-color - Text color of the table header. (default: var(--dds-text-neutral-strong))
 * @cssproperty --dds-datatable-row-hover-bg-color - Background color of hovered rows. (default: var(--dds-background-brand-strong))
 * @cssproperty --dds-datatable-row-selected-bg-color - Background color of selected rows. (default: var(--dds-background-brand-strong))
 * @cssproperty --dds-datatable-cell-padding - Padding of table cells. (default: var(--dds-spacing-200))
 * @cssproperty --dds-datatable-header-padding - Padding of header cells. (default: var(--dds-spacing-200))
 * @cssproperty --dds-datatable-last-column-padding - Padding of the last column. (default: var(--dds-spacing-500))
 * @cssproperty --dds-datatable-first-column-padding - Padding of the first column. (default: var(--dds-spacing-500))
 * @cssproperty --dds-datatable-border-width - Width of table borders. (default: var(--dds-border-width-base))
 * @cssproperty --dds-datatable-outer-border-width - Width of the outer container border only. (default: var(--dds-datatable-border-width))
 * @cssproperty --dds-datatable-header-border-width - Width of the header row bottom border only. (default: var(--dds-datatable-border-width))
 * @cssproperty --dds-datatable-row-border-width - Width of the row (body cell) bottom borders only. (default: var(--dds-datatable-border-width))
 * @cssproperty --dds-datatable-stripe-color - Background color for striped rows. (default: var(--dds-background-neutral-base))
 * @cssproperty --dds-datatable-border-radius - Border radius of the table. (default: var(--dds-radius-base))
 * @cssproperty --dds-datatable-shadow - Box shadow of the table. (default: none)
 * @cssproperty --dds-datatable-transition-duration - Duration of hover/selection transitions. (default: 0.2s)
 * @cssproperty --dds-datatable-z-index - Z-index of the table. (default: 1)
 * @cssproperty --dds-datatable-min-height - Minimum height of the table. (default: auto)
 * @cssproperty --dds-datatable-max-height - Maximum height of the table. (default: none)
 * @cssproperty --dds-datatable-overflow-x - Horizontal overflow behavior. (default: auto)
 * @cssproperty --dds-datatable-overflow-y - Vertical overflow behavior. (default: auto)
 */
export default class DapDSDataTable<T> extends DdsElement {
  static tagName = 'dap-ds-datatable'

  static dependencies = {
    'dap-ds-checkbox': DapDSCheckbox,
    'dap-ds-icon-arrow-up-s-line': ArrowsArrowUpSLine,
    'dap-ds-icon-arrow-down-s-line': ArrowsArrowDownSLine,
    'dap-ds-skeleton': DapDSSkeleton,
    'dap-ds-spinner': DapDSSpinner,
    'dap-ds-pager': DapDSPager,
  }

  static readonly styles = unsafeCSS(styles)

  private readonly tableController = new TableController<T>(this)

  // Pinned columns, recomputed each render: column id -> the edge it is pinned
  // to and the pixel offset from that edge. The offset accumulates the widths of
  // the columns pinned to the same side, so several pinned columns line up
  // instead of overlapping. Read by renderHeaderCell / renderBody.
  private stickyOffsets = new Map<
    string,
    { side: 'left' | 'right'; offset: number }
  >()

  /** Data to display in the table */
  @property({ attribute: false }) data: RowData[] = []
  /** Columns to display in the table */
  @property({ attribute: false }) columns: ExtendedColumnDef<T>[] = []

  /** Row key to use for row selection, this should be a unique key for each row */
  @property() rowKey: string = 'id'

  /** Enable row selection on the table, can be a boolean or a function that returns a boolean
   * @type {boolean | ((row: Row<T>) => boolean)}
   */
  @property({ type: Boolean }) enableRowSelection:
    | boolean
    | ((row: Row<T>) => boolean) = false
  /** Pin the built-in row-selection (checkbox) column to the left edge, so it
   * stays visible while the other columns scroll horizontally under it. */
  @property({ type: Boolean }) stickySelectionColumn: boolean = false
  /** Enable sorting on the table */
  @property({ type: Boolean }) enableSorting: boolean = false
  /** Enable manual sorting on the table */
  @property({ type: Boolean }) manualSorting: boolean = false
  /** Enables manual pagination. If this option is set to true, the table will not automatically paginate rows and instead will expect you to manually paginate the rows before passing them to the table. This is useful if you are doing server-side pagination and aggregation. */
  @property({ type: Boolean }) manualPagination: boolean = false
  /** If set to true, pagination will be reset to the first page when page-altering state changes eg. data is updated, filters change, grouping changes, etc. This behavior is automatically disabled when manualPagination is true but it can be overridden by explicitly assigning a boolean value to the autoResetPageIndex table option. */
  @property({ type: Boolean }) autoResetPageIndex: boolean = false

  /** Enable row click on the table */
  @property({ type: Boolean }) enableRowClick: boolean = false

  /** Loading state of the table */
  @property({ type: Boolean }) loading: boolean = false

  /** Whether to enable striped rows */
  @property({ type: Boolean }) enableStripedRows: boolean = false

  /** Number of rows in the table */
  @property({ type: Number }) rowCount?: number

  /** Caption text for the table */
  @property({ type: String }) caption?: string

  /** The type of loading to use
   * @type {'spinner' | 'skeleton'}
   */
  @property({ type: String }) loadingType: LoadingType = 'spinner'

  /**
   * The variant of the spinner. Only used if loadingType is 'spinner'.
   * @type {"neutral" | "brand" | "negative" | "positive" | "inverted"}
   */
  @property({ type: String, reflect: true }) loadingVariant: SpinnerVariant =
    'neutral'

  /**
   * The size of the spinner. Only used if loadingType is 'spinner'.
   * @type {"xxl" | "xl" | "lg" | "md" | "sm" | "xs"}
   */
  @property({ type: String, reflect: true }) loadingSize: IconSize = 'lg'
  /** The size of the spinner in pixels. This overrides the size attribute. Only used if loadingType is 'spinner'. */
  @property({ type: Number }) loadingStaticSize?: number
  /** The loading text. Only used if loadingType is 'spinner'. */
  @property({ type: String }) loadingText: string

  /** The text to display when the table is empty. */
  @property({ type: String }) emptyText: string

  /** Whether to disable the header when the table is empty.
   * @type {'true' | 'false'}
   */
  @property({ type: String }) disableHeaderOnEmpty: string = 'true'

  /** Whether to show the pager component when the table is empty. */
  @property({ type: Boolean }) showPagerOnEmpty: boolean = false

  /** Whether to show the pager component */
  @property({ type: Boolean }) pager: boolean = false

  /** Show the page size options. */
  @property({ type: String }) showPageSizeOptions = 'true'
  /** Show the page index. */
  @property({ type: String }) showPageIndex = 'true'
  /** Show the page count. */
  @property({ type: String }) showPageCount = 'true'
  /** Show the first button. */
  @property({ type: String }) showFirstButton = 'true'
  /** Show the previous button. */
  @property({ type: String }) showPreviousButton = 'true'
  /** Show the next button. */
  @property({ type: String }) showNextButton = 'true'
  /** Show the last button. */
  @property({ type: String }) showLastButton = 'true'
  /** Show numbered page buttons between the previous and next buttons. */
  @property({ type: String }) showPageNumbers = 'false'
  /** How many page numbers to show on each side of the current page. */
  @property({ type: Number }) siblingCount = 1

  /** The label of the first button */
  @property({ type: String }) firstButtonLabel: string

  /** The label of the previous button */
  @property({ type: String }) previousButtonLabel: string

  /** The label of the next button */
  @property({ type: String }) nextButtonLabel: string

  /** The label of the last button */
  @property({ type: String }) lastButtonLabel: string

  /** `data-testid` for the column sort-toggle button. Suffixed with the column id. */
  @property() sortButtonTestId = 'datatable-sort-button'

  /** The function to determine the pager text
   * @type {(pageIndex: number, pageSize: number, totalRows: number) => string}
   */
  @property({ attribute: false }) pageStateText: (
    pageIndex: number,
    pageSize: number,
    totalRows: number,
  ) => string

  /** Available page size options for the pager
   * @type {number[]}
   */
  @property({ type: Array }) pageSizeOptions: number[] = [10, 25, 50, 100]

  /** Sorting state of the table */
  @property({ type: Array }) sorting: SortingState
  /** @ignore */
  @state() private _sorting: SortingState = []

  /** Selection state of the table */
  @property({ type: Object }) rowSelection: RowSelectionState
  /** @ignore */
  @state() private _rowSelection: RowSelectionState = {}

  /** Pagination state of the table */
  @property({ type: Object }) pagination: PaginationState
  /** @ignore */
  @state() private _pagination: PaginationState = {
    pageIndex: 0,
    pageSize: 10,
  }

  /** Column sizing state of the table */
  @property({ type: Object }) columnSizing?: Record<string, number>
  /** @ignore */
  @state() private _columnSizing: Record<string, number> = {}

  /** ID of element labeling the table */
  @property({ type: String }) ariaLabel: string | null = null

  /** @ignore */
  private resizeObserver?: ResizeObserver

  /** @ignore */
  private get isTableEmpty(): boolean {
    // When using manual pagination with rowCount, check rowCount instead of data length
    if (this.manualPagination && this.rowCount !== undefined) {
      return this.rowCount === 0
    }
    return !this.data || this.data.length === 0
  }

  /** @ignore */
  private get shouldDisableHeaderActions(): boolean {
    return this.disableHeaderOnEmpty === 'true' && this.isTableEmpty
  }

  /** @ignore */
  @state() private readonly _statusMessage: string = ''

  /** @ignore */
  @property({ type: Boolean }) debug = false

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    if (this.sorting) {
      this._sorting = this.sorting
    }

    if (this.rowSelection) {
      this._rowSelection = this.rowSelection
    }

    if (this.pagination) {
      this._pagination = this.pagination
    } else if (this.pageSizeOptions && this.pageSizeOptions.length > 0) {
      // If pagination is not explicitly set, use the first item in pageSizeOptions as default
      this._pagination = {
        ...this._pagination,
        pageSize: this.pageSizeOptions[0],
      }
    }
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('pagination')) {
      this._pagination = this.pagination
    }

    if (_changedProperties.has('sorting')) {
      this._sorting = this.sorting
    }

    if (
      _changedProperties.has('rowSelection') ||
      _changedProperties.has('rowselection')
    ) {
      this._rowSelection = this.rowSelection
    }

    if (_changedProperties.has('data')) {
      this.requestUpdate()
    }
  }

  /**
   * Cleanup resize observer
   */
  disconnectedCallback(): void {
    super.disconnectedCallback()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = undefined
    }
  }

  private readonly columnsMemo = () => {
    // Process columns to prevent TanStack Table's default 150px width
    // Set minSize: 0 for columns without explicit sizes so they default to 'auto' in colgroup
    const processedColumns = this.columns.map(col => {
      // If column has no size specified, set minSize to 0 to prevent default 150px
      if (!col.size && !col.minSize && !col.maxSize) {
        return { ...col, minSize: 0 }
      }
      return col
    })

    return [
      ...(this.enableRowSelection &&
      this.columns[0] &&
      this.columns[0].id !== MULTISELECT_COLUMN_ID
        ? [
            {
              id: MULTISELECT_COLUMN_ID,
              accessorKey: MULTISELECT_COLUMN_ID,
              enableSorting: false,
              size: 50,
              maxSize: 50,
              ...(this.stickySelectionColumn
                ? { sticky: 'left' as const }
                : {}),
              header: ({ table }: { table: Table<T> }) =>
                html`<dap-ds-checkbox
                  isIndeterminate=${table.getIsSomeRowsSelected()}
                  @dds-change=${table.getToggleAllRowsSelectedHandler()}
                  .checked="${table.getIsAllRowsSelected()}"
                  .indeterminate="${table.getIsSomeRowsSelected()}"
                  ?disabled="${this.disableHeaderOnEmpty === 'true' &&
                  this.isTableEmpty}"></dap-ds-checkbox>`,
              cell: ({ row }: { row: any }) =>
                html`<dap-ds-checkbox
                  @dds-change="${row.getToggleSelectedHandler()}"
                  .checked="${row.getIsSelected()}"
                  ?disabled="${!row.getCanSelect()}"
                  .indeterminate="${row.getIsSomeSelected()}"></dap-ds-checkbox>`,
            },
          ]
        : []),
      ...processedColumns,
    ]
  }

  // Walks the visible leaf columns from each edge inward, giving every pinned
  // column its offset (the summed width of the already-seen pinned columns on
  // that side). getSize() resolves to the column's `size`, so a pinned column
  // without a size contributes 0 and the next one on that side would overlap it.
  private computeStickyOffsets(table: Table<T>) {
    const offsets = new Map<
      string,
      { side: 'left' | 'right'; offset: number }
    >()
    const columns = table.getVisibleLeafColumns()

    let right = 0
    for (let i = columns.length - 1; i >= 0; i--) {
      const column = columns[i]
      if ((column.columnDef as ExtendedColumnDef<T>).sticky === 'right') {
        offsets.set(column.id, { side: 'right', offset: right })
        right += column.getSize()
      }
    }

    let left = 0
    for (const column of columns) {
      if ((column.columnDef as ExtendedColumnDef<T>).sticky === 'left') {
        offsets.set(column.id, { side: 'left', offset: left })
        left += column.getSize()
      }
    }

    this.stickyOffsets = offsets
  }

  // Inline sticky positioning for a pinned column. z-index lifts the cell above
  // the row cells scrolling under it (needed for a left-pinned column, whose
  // siblings paint after it). The opaque background and edge divider come from
  // the `--sticky-*` classes in the stylesheet.
  private getStickyStyle(columnId: string): string | undefined {
    const sticky = this.stickyOffsets.get(columnId)
    if (!sticky) return undefined
    return `position: sticky; ${sticky.side}: ${sticky.offset}px; z-index: 1;`
  }

  private renderHeaderCell(
    header: Header<T, unknown>,
    _isLastColumn: boolean = false,
  ) {
    const columnDef = header.column.columnDef as ExtendedColumnDef<T>
    const canSort = header.column.getCanSort()
    const isSorted = header.column.getIsSorted()
    const nextSortOrder = header.column.getNextSortingOrder()
    const disableDefaultSorting = columnDef.disableDefaultSorting ?? false

    const sticky = this.stickyOffsets.get(header.column.id)
    const headerStyle =
      [this.getHeaderCellStyle(header), this.getStickyStyle(header.column.id)]
        .filter(Boolean)
        .join(' ') || undefined
    const contentStyle = columnDef.headerTextAlign
      ? `justify-content: ${columnDef.headerTextAlign};`
      : undefined

    const sortIcon = this.getSortIcon(isSorted)
    const sortTitle = this.getSortTitle(canSort, nextSortOrder)

    return html`
      <th
        colspan="${header.colSpan}"
        rowspan="${header.rowSpan}"
        part="header-cell"
        class=${clsx(
          'data-table-header-cell',
          sticky && `data-table-header-cell--sticky-${sticky.side}`,
        )}
        scope="col"
        aria-sort=${canSort && !this.shouldDisableHeaderActions
          ? this.getAriaSortValue(isSorted)
          : 'none'}
        style="${ifDefined(headerStyle)}">
        ${this.renderHeaderCellContent(
          header,
          columnDef,
          canSort,
          disableDefaultSorting,
          sortTitle,
          contentStyle,
          sortIcon,
        )}
      </th>
    `
  }

  private renderHeaderCellContent(
    header: Header<T, unknown>,
    columnDef: ExtendedColumnDef<T>,
    canSort: boolean,
    disableDefaultSorting: boolean,
    sortTitle: string | undefined,
    contentStyle: string | undefined,
    sortIcon: any,
  ) {
    if (!canSort) {
      return flexRender(columnDef.header, header.getContext())
    }

    // Disable sorting if disableHeaderOnEmpty is true and table is empty
    const elementTag =
      disableDefaultSorting || this.shouldDisableHeaderActions
        ? 'div'
        : 'dap-ds-button'
    const clickHandler =
      !disableDefaultSorting && !this.shouldDisableHeaderActions
        ? header.column.getToggleSortingHandler()
        : null
    const cursorStyle =
      canSort && !this.shouldDisableHeaderActions ? 'pointer' : 'not-allowed'

    return html`<${unsafeStatic(elementTag)}
      class="data-table-header-cell__controller"
      variant="clean"
      data-testid="${this.sortButtonTestId}-${header.column.id}"
      title=${ifDefined(sortTitle)}
      @click="${clickHandler}"
      style="cursor: ${cursorStyle}">
      <span 
        part="content" 
        class="data-table-header-cell__content"
        style="${ifDefined(contentStyle)}">
        ${flexRender(columnDef.header, header.getContext())}
        ${sortIcon}
      </span>
    </${unsafeStatic(elementTag)}>`
  }

  private getHeaderCellStyle(header: Header<T, unknown>): string | undefined {
    const columnDef = header.column.columnDef as ExtendedColumnDef<T>
    if (!columnDef.size && !columnDef.maxSize && !columnDef.headerTextAlign) {
      return undefined
    }

    return `
      ${columnDef.size ? `width: ${columnDef.size}px;` : ''}
      ${columnDef.maxSize ? `max-width: ${columnDef.maxSize}px;` : ''}
      ${columnDef.headerTextAlign ? `text-align: ${columnDef.headerTextAlign};` : ''}
    `
  }

  // Replace this union type with a type alias.
  private getSortIcon(isSorted: SortState) {
    if (isSorted === 'asc') {
      return html`<dap-ds-icon-arrow-up-s-line
        size="16"></dap-ds-icon-arrow-up-s-line>`
    }
    if (isSorted === 'desc') {
      return html`<dap-ds-icon-arrow-down-s-line
        size="16"></dap-ds-icon-arrow-down-s-line>`
    }
    return null
  }

  private getSortTitle(
    canSort: boolean,
    nextSortOrder: false | 'asc' | 'desc',
  ): string | undefined {
    if (!canSort) return undefined

    if (nextSortOrder === 'asc') return t('datatable.sortAscending')
    if (nextSortOrder === 'desc') return t('datatable.sortDescending')
    return t('datatable.clearSort')
  }

  private getAriaSortValue(
    isSorted: SortState,
  ): 'ascending' | 'descending' | 'none' {
    if (isSorted === 'asc') return 'ascending'
    if (isSorted === 'desc') return 'descending'
    return 'none'
  }

  /**
   * Render cell content with support for multiple formats
   * Supports: primitives, styled objects, HTMLElements, HTML strings,
   * render callbacks, and Lit templates
   */
  private renderCell(cell: Cell<T, unknown>) {
    const content = cell.column.columnDef.cell
    const context = cell.getContext()

    // Get raw content from cell renderer
    const rawContent =
      typeof content === 'function' ? content(context) : content

    // Process through multi-format renderer
    const processed = renderCellContent(rawContent)

    // If it's already a Lit template or primitive, use flexRender
    // Otherwise, return the processed content directly
    if (
      typeof processed === 'string' ||
      typeof processed === 'number' ||
      typeof processed === 'boolean' ||
      processed === null ||
      processed === undefined
    ) {
      return processed
    }

    // For HTMLElement or complex content, wrap in lit-html unsafeHTML
    if (processed instanceof HTMLElement) {
      // Return the element directly - Lit can handle HTMLElements
      return processed
    }

    // For everything else (TemplateResult), use flexRender
    return flexRender(content, context)
  }

  private handleRowClick(row: Row<T>): void {
    if (this.enableRowClick) {
      this.emit('dds-row-click', { row })
    }
  }

  private handleRowKeydown(e: KeyboardEvent, row: Row<T>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      // Don't prevent default or handle row click if the event originated from
      // an interactive element inside the cell (button, link, input, etc.)
      // Use composedPath() to check the actual originating element across shadow DOM boundaries
      const composedPath = e.composedPath()

      // Check if any element in the composed path is an interactive element
      const isInteractiveElement = composedPath.some(element => {
        if (!(element instanceof HTMLElement)) return false
        // Check native elements
        if (
          ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(
            element.tagName,
          )
        ) {
          return true
        }
        // Check role="button"
        if (element.getAttribute('role') === 'button') {
          return true
        }
        // Check for custom button elements (like dap-ds-button)
        // These typically have a button-like tag name or contain 'button' in their name
        if (
          element.tagName.toLowerCase().includes('button') ||
          element.tagName.toLowerCase().includes('btn')
        ) {
          return true
        }
        return false
      })

      if (isInteractiveElement) {
        // Let the interactive element handle the keyboard event naturally
        return
      }

      e.preventDefault()
      this.handleRowClick(row)
    }
  }

  private readonly renderColgroup = (_table: Table<T>) => {
    const columns = this.columnsMemo()
    const columnSizing = this.columnSizing || this._columnSizing

    return html`
      <colgroup>
        ${repeat(
          columns,
          (col, index) => col.id || `col-${index}`,
          col => {
            const columnDef = col as ExtendedColumnDef<T>
            let width: string

            // Use calculated sizing if available (from widthPercentage or explicit size)
            if (columnSizing && columnDef.id && columnSizing[columnDef.id]) {
              width = `${columnSizing[columnDef.id]}px`
            }
            // Use explicit size from column definition
            else if (columnDef.size) {
              width = `${columnDef.size}px`
            }
            // Default to 'auto' for flexible columns (no explicit size or widthPercentage)
            else {
              width = 'auto'
            }

            return html`<col style="width: ${width};" />`
          },
        )}
      </colgroup>
    `
  }

  private readonly renderHeader = (table: Table<T>) => {
    return html`
      <thead part="header">
        ${repeat(
          table.getHeaderGroups(),
          headerGroup => headerGroup.id,
          headerGroup => html`
            <tr
              part="header-row"
              class=${clsx('data-table-header-row', {
                'data-table-header-row--striped': this.enableStripedRows,
              })}>
              ${repeat(
                headerGroup.headers,
                header => header.id,
                header => this.renderHeaderCell(header),
              )}
            </tr>
          `,
        )}
      </thead>
    `
  }

  private readonly renderSkeletonRows = (
    rowCount: number,
    cellCount: number,
  ) => {
    return html`
      ${repeat(
        Array.from({ length: rowCount }, (_, index) => index),
        index => index,
        _index => html`
          <tr class="data-table-row">
            ${repeat(
              Array.from({ length: cellCount }, (_, index) => index),
              index => index,
              _index =>
                html`<td class="data-table-cell">
                  <dap-ds-skeleton></dap-ds-skeleton>
                </td>`,
            )}
          </tr>
        `,
      )}
    `
  }

  private readonly renderBody = (table: Table<T>) => {
    return html`
      ${this.loading
        ? html`<tbody part="body">
            ${this.loadingType === 'spinner'
              ? html`
                  <tr>
                    <td
                      colspan="${this.columnsMemo().length}"
                      class="data-table-cell">
                      <div part="loading" class="data-table-placeholder">
                        <slot name="loading">
                          <dap-ds-spinner
                            size=${this.loadingSize}
                            variant=${this.loadingVariant}
                            .staticSize=${this.loadingStaticSize}
                            .text=${this.loadingText}></dap-ds-spinner>
                        </slot>
                      </div>
                    </td>
                  </tr>
                `
              : html`
                  ${this.renderSkeletonRows(
                    this._pagination.pageSize ?? 10,
                    this.columnsMemo().length,
                  )}
                `}
          </tbody>`
        : html`
            <tbody part="body">
              ${this.isTableEmpty
                ? html`<tr class="data-table-row data-table-row--empty">
                    <td
                      colspan="${this.columnsMemo().length}"
                      class="data-table-cell">
                      <div part="empty" class="data-table-placeholder">
                        <slot name="empty">
                          <span
                            >${this.emptyText ?? t('datatable.noData')}</span
                          >
                        </slot>
                      </div>
                    </td>
                  </tr>`
                : table.getRowModel().rows.map(
                    (row, index) => html`
                      <tr
                        part="row"
                        role=${this.enableRowClick ? 'button' : 'row'}
                        tabindex=${this.enableRowClick ? 0 : -1}
                        aria-selected=${row.getIsSelected()}
                        aria-rowindex=${index + 2}
                        class=${clsx('data-table-row', {
                          'data-table-row--selected': row.getIsSelected(),
                          'data-table-row--clickable': this.enableRowClick,
                          'data-table-row--striped': this.enableStripedRows,
                        })}
                        @click=${() => this.handleRowClick(row)}
                        @keydown=${(e: KeyboardEvent) =>
                          this.handleRowKeydown(e, row)}>
                        ${row.getVisibleCells().map(cell => {
                          const columnDef = cell.column
                            .columnDef as ExtendedColumnDef<T>
                          const sticky = this.stickyOffsets.get(cell.column.id)
                          const cellStyle =
                            [
                              columnDef.textAlign &&
                                `text-align: ${columnDef.textAlign};`,
                              this.getStickyStyle(cell.column.id),
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined
                          return html`
                            <td
                              part="cell"
                              class=${clsx(
                                'data-table-cell',
                                sticky &&
                                  `data-table-cell--sticky-${sticky.side}`,
                              )}
                              style="${ifDefined(cellStyle)}">
                              ${this.renderCell(cell)}
                            </td>
                          `
                        })}
                      </tr>
                    `,
                  )}
            </tbody>
          `}
    `
  }

  protected render() {
    const table = this.tableController.table({
      data: this.data as T[],
      columns: this.columnsMemo(),
      filterFns: {},
      state: {
        sorting: this._sorting,
        rowSelection: this._rowSelection,
        pagination: this._pagination,
      },
      manualSorting: this.manualSorting,
      enableSorting: this.enableSorting,
      enableRowSelection: this.enableRowSelection,
      manualPagination: this.manualPagination,
      autoResetPageIndex: this.autoResetPageIndex,
      onSortingChange: updaterOrValue => {
        if (typeof updaterOrValue === 'function') {
          this._sorting = updaterOrValue(this._sorting)
        } else {
          this._sorting = updaterOrValue
        }

        this.emit('dds-sorting-change', { sorting: this._sorting })
      },
      getCoreRowModel: getCoreRowModel(),
      ...(!this.manualSorting && {
        getSortedRowModel: getSortedRowModel(),
      }),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: updaterOrValue => {
        if (typeof updaterOrValue === 'function') {
          this._pagination = updaterOrValue(this._pagination)
        } else {
          this._pagination = updaterOrValue
        }

        this.emit('dds-pagination-change', {
          pagination: {
            pageIndex: this._pagination.pageIndex,
            pageSize: this._pagination.pageSize,
          },
        })
      },
      onRowSelectionChange: updaterOrValue => {
        if (typeof updaterOrValue === 'function') {
          this._rowSelection = updaterOrValue(this._rowSelection)
        } else {
          this._rowSelection = updaterOrValue
        }

        this.emit('dds-selection-change', { selection: this._rowSelection })
      },
      getRowId: originalRow => {
        return (originalRow as Record<string, any>)[this.rowKey]
      },
      rowCount: this.rowCount ?? (this.data ? this.data.length : 0),
      debugTable: this.debug,
      debugHeaders: this.debug,
      debugColumns: this.debug,
    })

    // Resolve pinned-column offsets before rendering the header/body cells that
    // read them off this.stickyOffsets.
    this.computeStickyOffsets(table)

    return html`
      <div class="data-table-container">
        <table
          part="base"
          role="table"
          aria-label=${ifDefined(
            this.ariaLabel ?? t('datatable.tableDescription'),
          )}
          aria-describedby=${ifDefined(this.ariaDescribedBy)}
          aria-rowcount=${this.rowCount ?? (this.data ? this.data.length : 0)}
          class=${clsx('data-table', {
            'data-table--loading': this.loading,
          })}>
          ${this.caption
            ? html`<caption class="visually-hidden">
                ${this.caption}
              </caption>`
            : nothing}
          ${this.renderColgroup(table)} ${this.renderHeader(table)}
          ${this.renderBody(table)}
        </table>
      </div>
      ${this._statusMessage
        ? html`<div
            aria-live="polite"
            aria-atomic="true"
            class="visually-hidden"
            id="table-status">
            ${this._statusMessage}
          </div>`
        : nothing}
      ${(this.pager && !this.isTableEmpty) ||
      (this.pager && this.showPagerOnEmpty && this.isTableEmpty)
        ? html`<dap-ds-pager
            class="data-table-pager"
            part="pager"
            .disabled=${this.isTableEmpty}
            .manualPagination=${this.manualPagination}
            .pageIndex=${table.getState().pagination.pageIndex}
            .pageSize=${table.getState().pagination.pageSize}
            .totalRows=${this.rowCount ?? (this.data ? this.data.length : 0)}
            .pageSizeOptions=${this.pageSizeOptions}
            .showPageSizeOptions=${this.showPageSizeOptions}
            .showPageIndex=${this.showPageIndex}
            .showPageCount=${this.showPageCount}
            .showFirstButton=${this.showFirstButton}
            .showPreviousButton=${this.showPreviousButton}
            .showNextButton=${this.showNextButton}
            .showLastButton=${this.showLastButton}
            .showPageNumbers=${this.showPageNumbers}
            .siblingCount=${this.siblingCount}
            .firstButtonLabel=${this.firstButtonLabel}
            .previousButtonLabel=${this.previousButtonLabel}
            .nextButtonLabel=${this.nextButtonLabel}
            .lastButtonLabel=${this.lastButtonLabel}
            .pageStateText=${this.pageStateText}
            exportparts="base:pager-base, first:pager-first, previous:pager-previous, next:pager-next, last:pager-last, page-size:pager-page-size-select, page-size-select-base:page-size-select-base, page-size-select-trigger:page-size-select-trigger, pager-button-first-base:pager-button-first-base, pager-button-first-content:pager-button-first-content, pager-button-previous-base:pager-button-previous-base, pager-button-previous-content:pager-button-previous-content, pager-button-next-base:pager-button-next-base, pager-button-next-content:pager-button-next-content, pager-button-last-base:pager-button-last-base, pager-button-last-content:pager-button-last-content, page-numbers:pager-page-numbers, page-number:pager-page-number, page-number-active:pager-page-number-active, page-number-base:pager-page-number-base, page-number-content:pager-page-number-content, page-info:pager-page-info"
            @dds-pagination-change=${(e: any) => {
              e.preventDefault()
              e.stopPropagation()

              if (e.detail.action === 'pageSize') {
                table.setPageSize(e.detail.pagination.pageSize)
              } else {
                table.setPageIndex(e.detail.pagination.pageIndex)
              }
            }}>
            <slot name="first-button-label" slot="first-button-label">
              ${this.firstButtonLabel || t('pager.first')}
            </slot>
            <slot name="previous-button-label" slot="previous-button-label">
              ${this.previousButtonLabel || t('pager.previous')}
            </slot>
            <slot name="next-button-label" slot="next-button-label">
              ${this.nextButtonLabel || t('pager.next')}
            </slot>
            <slot name="last-button-label" slot="last-button-label">
              ${this.lastButtonLabel || t('pager.last')}
            </slot>
          </dap-ds-pager>`
        : nothing}
    `
  }
}
